// GET/POST /v1/apps, GET /v1/apps/:id (contracts.md:52). RECONCILED at Wave-3 integration against
// the real apps/api response: every read parses the shared tenant-safe `AppView` projection (NOT the
// full `App`, which carries server-only cost/lifecycle fields the API never returns to the SPA). POST
// captures name + type + origin + intent + optional free text; see src/state/wizard.ts for the step
// order (type -> intent -> connect), a resolved conflict with the UX spec cited there in full.
import { z } from "zod";
import { AppId, AppIntent, AppOrigin, AppType, AppView, NonEmptyString } from "@nightcrew/shared";
import type { Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

export const AppList = z.object({ apps: z.array(AppView) });
export type AppList = z.infer<typeof AppList>;

export const listApps = (): Promise<Result<AppList, ClientError>> => apiFetch("/v1/apps", AppList);

export const getApp = (appId: AppId): Promise<Result<AppView, ClientError>> =>
  apiFetch(`/v1/apps/${appId}`, AppView);

/**
 * The add-app wizard's screens 1+2 combined into one create call. `name` is required by the API
 * (the founder names the app on the intent step); `intentFreeText` is OMITTED when empty (the API
 * field is optional/undefined, not nullable).
 */
export const CreateAppRequest = z.object({
  name: NonEmptyString,
  type: AppType,
  origin: AppOrigin,
  intent: AppIntent,
  intentFreeText: NonEmptyString.optional(),
});
export type CreateAppRequest = z.infer<typeof CreateAppRequest>;

export const createApp = (request: CreateAppRequest): Promise<Result<AppView, ClientError>> =>
  apiPost("/v1/apps", AppView, request);
